import { useSettings } from '@/settings/hooks/useSettings'
import { useState, useEffect } from 'react'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { Header } from '@/components/header/Header'
import DefaultJurisdictionSettingsDialog from '@/settings/components/DefaultJurisdictionSettingsDialog'
import { getJurisdictionLabel } from '@/jurisdiction-mappings/jurisdiction-maps'
import { getDefaultJurisdictionAnalyticsProps } from '@/analytics/utils/matter-jurisdiction-analytics'
import { Button } from '@/components/button/Button'
import { CircularProgressContinuousSized } from '@/components/loaders/CircularProgressContinuous'
import ConnectedAccounts from '@/settings/components/ConnectedAccounts'
import CustomInstructionsCard from '@/settings/components/CustomInstructionsCard'
import { getCustomInstructionsAnalyticsProps } from '@/analytics/utils/custom-instructions-analytics'
import SectionHeader from '@/settings/components/SectionHeader'
import { kFeatureFlags, kGetFeatureFlagStatus } from '@/constants/constants-feature-flags'

export default function SettingsPage() {
  const { getSettings, updateUserSettings, defaultJurisdiction, customInstructions, settingsLoaded } = useSettings()
  const [jurisdictionDialogOpen, setJurisdictionDialogOpen] = useState(false)
  const { trackEvent } = useAnalytics()

  useEffect(() => {
    getSettings()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps -- Keep the existing mount-only settings fetch behavior.

  const handleSaveCustomInstructions = async (nextInstructions: string) => {
    await updateUserSettings({ custom_instructions: nextInstructions })
    trackEvent(AnalyticsEvent.CustomInstructionsSaved, getCustomInstructionsAnalyticsProps(nextInstructions))
  }

  const handleSaveDefaultJurisdiction = async (jurisdiction: string | null) => {
    await updateUserSettings({ default_jurisdiction: jurisdiction })
    trackEvent(AnalyticsEvent.DefaultJurisdictionSaved, getDefaultJurisdictionAnalyticsProps(jurisdiction))
  }

  const handleConfigureDefaultJurisdiction = () => {
    trackEvent(AnalyticsEvent.DefaultJurisdictionConfigureClicked, { hasExistingDefaultJurisdiction: Boolean(defaultJurisdiction) })
    setJurisdictionDialogOpen(true)
  }

  return (
    <div className="grow">
      <Header title="Settings" />

      <div className="flex justify-center px-4 sm:px-8 md:px-10 mt-6">
        <div className="grow max-w-[946px] flex flex-col gap-6">
          <div className="flex flex-col overflow-hidden rounded-lg border border-border-default bg-surface-emphasis">
            <SectionHeader title="Matter Settings" />
            <div className="py-4">
              <div className="flex flex-col gap-3 px-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <p className="font-semibold leading-7 text-text-primary">Default Jurisdiction</p>
                  {!settingsLoaded ? (
                    <div className="flex min-h-5 items-center" role="status" aria-label="Loading default jurisdiction">
                      <CircularProgressContinuousSized size={14} thickness={6} />
                    </div>
                  ) : defaultJurisdiction ? (
                    <div className="flex flex-wrap items-center gap-x-2.5 text-sm leading-5 tracking-[-0.15px]">
                      <span className="font-semibold text-text-secondary">{getJurisdictionLabel(defaultJurisdiction)}</span>
                      <span className="hidden h-3.5 border-l border-brand-neutral-400 sm:inline-block" aria-hidden="true" />
                      <span className="text-text-tertiary">You can change this when creating each matter.</span>
                    </div>
                  ) : (
                    <p className="text-sm leading-5 tracking-[-0.15px] text-text-tertiary">
                      Select a jurisdiction to auto-fill new matters. You can override anytime during matter creation.
                    </p>
                  )}
                </div>
                <Button
                  variant="tonal"
                  color={defaultJurisdiction ? 'neutral' : 'primary'}
                  size="sm"
                  onClick={handleConfigureDefaultJurisdiction}
                  disabled={!settingsLoaded}
                  className="self-start sm:self-auto"
                >
                  {defaultJurisdiction ? 'Edit' : 'Configure'}
                </Button>
              </div>
            </div>
          </div>

          {kGetFeatureFlagStatus(kFeatureFlags.DRIVE_SYNC) && <ConnectedAccounts />}

          <CustomInstructionsCard savedInstructions={customInstructions} settingsLoaded={settingsLoaded} onSave={handleSaveCustomInstructions} />
        </div>
      </div>

      <DefaultJurisdictionSettingsDialog
        open={jurisdictionDialogOpen}
        onOpenChange={setJurisdictionDialogOpen}
        initialJurisdiction={defaultJurisdiction}
        onSave={handleSaveDefaultJurisdiction}
      />
    </div>
  )
}
